import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { loanManagementUrl, mpesaServiceUrls, companyManagementUrl } from "../../config";
import DynamicTableForJson from "../../data/DynamicTable/dynamicTableForJson";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { walletQuery, WALLET_QUERY } from "../../data/WalletService/walletGraphQL";
import { tokens } from "../../theme";

const MAX_BALANCE_POLL_ATTEMPTS = 6;
const BALANCE_POLL_INTERVAL_MS = 5000;

const getAuthHeader = () => {
  const raw = localStorage.getItem("token");
  return raw ? `Bearer ${raw.replace(/"/g, "")}` : "";
};

const base_url = loanManagementUrl.uri;
const comp_url = companyManagementUrl.uri;

const APPRAISAL_PARAMETERS = {
  isCompany: 1,
  isRegion: 0,
  isBranch: 0,
  isFieldOfficer: 0,
};

const CompanyLoanAppraisal = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id || 1, 10);
  const roleGroupId = parseInt(decodedToken.RoleGroupId || 1, 10);
  const companyId = parseInt(decodedToken.CompanyId || 1, 10);
  const navigate = useNavigate();

  const [walletInfo, setWalletInfo] = useState({
    availableBalance: 450000.0,
    escrowedBalance: 15375.0,
    isBelowThreshold: false,
    loading: false,
  });
  const [isSababishaPaybillEnabled, setIsSababishaPaybillEnabled] = useState(false);


  useEffect(() => {
    if (!companyId || !isSababishaPaybillEnabled) return;
    const fetchWallet = async () => {
      try {
        const res = await walletQuery(WALLET_QUERY, { companyId });
        const data = res?.wallet;
        if (data) {
          setWalletInfo({
            availableBalance: data.availableBalance ?? 450000.0,
            escrowedBalance: data.escrowedBalance ?? 15375.0,
            isBelowThreshold: !!(
              data.minimumRetainedBalance != null &&
              data.availableBalance < data.minimumRetainedBalance
            ),
            loading: false,
          });
        }
      } catch (err) {
        // keep defaults
      }
    };
    fetchWallet();
  }, [companyId, isSababishaPaybillEnabled]);

  // One entry per distinct paybill: { branchId, branchNames, utility, working, receivedAt, refreshing, error }
  const [liveBalances, setLiveBalances] = useState([]);
  const [balanceLoadError, setBalanceLoadError] = useState(null);
  const [totalToDisburse, setTotalToDisburse] = useState(null);
  const [loadingTotalToDisburse, setLoadingTotalToDisburse] = useState(true);

  useEffect(() => {
    async function fetchCompanySettings() {
      try {
        const response = await fetchJsonDynamic("companySettings", null, null, comp_url);
        if (response?.results) {
          const settings =
            typeof response.results === "string"
              ? JSON.parse(response.results)
              : response.results;
          if (Array.isArray(settings)) {
            const paybillSetting = settings.find(
              (s) => (s.Setting || "").trim().toUpperCase() === "USE_SABABISHA_PAYBILL"
            );
            const isEnabled =
              (paybillSetting?.Value ?? "").toString().trim().toLowerCase() === "true";
            setIsSababishaPaybillEnabled(isEnabled);
          }
        }
      } catch (error) {
        console.error("Error fetching company settings:", error);
        setIsSababishaPaybillEnabled(false);
      }
    }
    fetchCompanySettings();
  }, []);
  // Each distinct paybill polls independently — keyed by branchId ("default" for a company-wide,
  // branch-less paybill), so one paybill resolving/timing out never affects another's polling.
  const balancePollsRef = useRef(new Map());
  const branchKey = (branchId) => (branchId == null ? "default" : String(branchId));

  const stopBalancePoll = (key) => {
    const poll = balancePollsRef.current.get(key);
    if (poll?.timer) clearInterval(poll.timer);
    balancePollsRef.current.delete(key);
  };

  const stopAllBalancePolls = () => {
    balancePollsRef.current.forEach((poll) => {
      if (poll.timer) clearInterval(poll.timer);
    });
    balancePollsRef.current.clear();
  };

  const updateBalanceEntry = (branchId, patch) => {
    setLiveBalances((prev) => {
      const key = branchKey(branchId);
      const idx = prev.findIndex((e) => branchKey(e.branchId) === key);
      if (idx === -1) {
        return [
          ...prev,
          {
            branchId,
            branchNames: [],
            utility: null,
            working: null,
            receivedAt: null,
            refreshing: false,
            error: null,
            ...patch,
          },
        ];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const pollAccountBalanceStatus = useCallback((branchId, branchNames, conversationId) => {
    const key = branchKey(branchId);
    const poll = balancePollsRef.current.get(key) ?? {};
    poll.count = (poll.count ?? 0) + 1;
    balancePollsRef.current.set(key, poll);

    fetch(mpesaServiceUrls.accountBalanceStatus, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ ConversationId: conversationId }),
    })
      .then(async (resp) => {
        if (resp.status === 200) {
          const body = await resp.json();
          stopBalancePoll(key);
          updateBalanceEntry(branchId, {
            branchNames,
            utility: body.utilityAccountAvailableFunds,
            working: body.workingAccountAvailableFunds,
            receivedAt: body.receivedAt,
            refreshing: false,
            error: null,
          });
          return;
        }

        // 202 = still pending — keep polling unless attempts are exhausted
        if (poll.count >= MAX_BALANCE_POLL_ATTEMPTS) {
          stopBalancePoll(key);
          updateBalanceEntry(branchId, {
            refreshing: false,
            error: "No response from Safaricom yet.",
          });
        }
      })
      .catch((err) => {
        stopBalancePoll(key);
        updateBalanceEntry(branchId, {
          refreshing: false,
          error: `Error checking balance: ${err.message}`,
        });
      });
  }, []);

  const triggerLiveBalanceRefresh = useCallback(
    async (forCompanyId) => {
      if (!forCompanyId) return;
      stopAllBalancePolls();
      setBalanceLoadError(null);
      setLiveBalances((prev) => prev.map((e) => ({ ...e, refreshing: true, error: null })));

      try {
        const resp = await fetch(mpesaServiceUrls.queryAccountBalance, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: getAuthHeader(),
          },
          body: JSON.stringify({ CompanyId: forCompanyId }),
        });

        if (!resp.ok) {
          setLiveBalances((prev) => prev.map((e) => ({ ...e, refreshing: false })));
          setBalanceLoadError("Could not reach Safaricom to refresh the balance.");
          return;
        }

        const items = await resp.json();
        if (!Array.isArray(items) || items.length === 0) {
          setLiveBalances((prev) => prev.map((e) => ({ ...e, refreshing: false })));
          setBalanceLoadError("No paybill is configured for this company.");
          return;
        }

        items.forEach((item) => {
          const branchId = item.branchId ?? null;
          const branchNames = item.branchNames ?? [];
          const conversationId = item.conversationID;
          const key = branchKey(branchId);

          if (!conversationId) {
            updateBalanceEntry(branchId, {
              branchNames,
              refreshing: false,
              error: item.responseDescription || "Safaricom did not return a tracking ID.",
            });
            return;
          }

          updateBalanceEntry(branchId, { branchNames, refreshing: true, error: null });

          const timer = setInterval(() => {
            pollAccountBalanceStatus(branchId, branchNames, conversationId);
          }, BALANCE_POLL_INTERVAL_MS);
          balancePollsRef.current.set(key, { count: 0, timer });
        });
      } catch (err) {
        setLiveBalances((prev) => prev.map((e) => ({ ...e, refreshing: false })));
        setBalanceLoadError(`Error refreshing balance: ${err.message}`);
      }
    },
    [pollAccountBalanceStatus],
  );

  const seedLastKnownBalance = useCallback(async (forCompanyId) => {
    if (!forCompanyId) return;
    try {
      const resp = await fetch(mpesaServiceUrls.latestAccountBalance, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getAuthHeader(),
        },
        body: JSON.stringify({ CompanyId: forCompanyId }),
      });
      if (resp.status === 200) {
        const items = await resp.json();
        if (Array.isArray(items) && items.length > 0) {
          setLiveBalances(
            items.map((item) => ({
              branchId: item.branchId ?? null,
              branchNames: item.branchNames ?? [],
              utility: item.utilityAccountAvailableFunds,
              working: item.workingAccountAvailableFunds,
              receivedAt: item.receivedAt,
              refreshing: false,
              error: null,
            })),
          );
        }
      }
    } catch (err) {
      console.error("Failed to seed last known account balance:", err);
    }
  }, []);

  useEffect(() => {
    return () => stopAllBalancePolls();
  }, []);

  useEffect(() => {
    if (!companyId) return;
    seedLastKnownBalance(companyId);
    triggerLiveBalanceRefresh(companyId);
  }, [companyId]);

  const fetchTotalToDisburse = useCallback(async () => {
    setLoadingTotalToDisburse(true);
    try {
      const response = await fetchJsonDynamic(
        "allLoanApplications",
        "input",
        APPRAISAL_PARAMETERS,
        base_url,
      );
      if (response?.results) {
        const parsed = JSON.parse(response.results);
        if (Array.isArray(parsed)) {
          const total = parsed.reduce(
            (sum, loan) => sum + (parseFloat(loan.AmountToDisburse) || 0),
            0,
          );
          setTotalToDisburse(total);
        }
      }
    } catch (err) {
      console.error("Failed to fetch total amount to disburse:", err);
    } finally {
      setLoadingTotalToDisburse(false);
    }
  }, []);

  useEffect(() => {
    fetchTotalToDisburse();
  }, [fetchTotalToDisburse]);

  // const [isCompany, setIsCompany] = useState("0");
  // const [isRegion, setIsRegion] = useState("0");
  // const [isBranch, setIsBranch] = useState("0");
  // const [isFieldOfficer, setIsFieldOfficer] = useState("0");

  // const rightId = localStorage.getItem("RightIdFromSidebar");

  // // Use useEffect to update state properly
  // useEffect(() => {
  //   if (rightId === "23") {
  //     setIsCompany("1");
  //   } else if (rightId === "22") {
  //     setIsRegion("1");
  //   } else if (rightId === "21") {
  //     setIsBranch("1");
  //   } else if (rightId === "20") {
  //     setIsFieldOfficer("1");
  //   }
  // }, [rightId]); // Runs only when rightId changes

  const columns = [
    { field: "Id", headerName: "ID", flex: 1 },
    { field: "CustomerId", headerName: "Customer ID", flex: 1 },
    { field: "CustomerPhone", headerName: "Customer Phone", flex: 1 },
    { field: "FirstName", headerName: "First Name", flex: 1 },
    { field: "OtherNames", headerName: "Other Names", flex: 2 },
    { field: "ApplicationDate", headerName: "Booked Date", flex: 1.5 },
    { field: "AmountToDisburse", headerName: "Amount to Disburse", flex: 1 },
    { field: "InterestAmount", headerName: "Interest Amount", flex: 1 },
    { field: "LoanAmount", headerName: "Loan Amount", flex: 1 },
    // { field: "LoanBalance", headerName: "Loan Balance", flex: 1 },
    { field: "ServicedLoans", headerName: "Serviced Loans", flex: 1 },

    { field: "InstallmentAmount", headerName: "Installment Amount", flex: 1.2 },
    { field: "Name", headerName: "Branch", flex: 1 },
    {
      field: "RegionApprovedDate",
      headerName: "Region Approval Date",
      flex: 1.5,
    },
  ];
  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: false,
      permission: "CreatePermission",
    },
    ApproveReject: {
      key: "SETLOANLIMIT",
      button_name: "Approve/Reject loan",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
    },
    dataObject: {
      objectName: "allLoanApplications",
      inputObjectName: "input",
      parameters: APPRAISAL_PARAMETERS,
      ignoreFields: [
        "LoanDisbursed",
        "Installments",
        "InstallmentAmount",
        "IsLoanApproved",
        "IsBranchApproved",
        "AverageCashSales",
        "FoFirstName",
        "FoLastName",
      ],
    },
    showStatus: true,
  };

  const formattedBalance = (value) => {
    if (value === null || value === undefined) return "—";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box m="20px">
      {/* Summary cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
        <Tooltip
          title="Live M-PESA Utility Account balance, queried directly from Safaricom. Always verify that this balance is sufficient before disbursing loans — disbursing without adequate funds will result in failed or blank disbursements."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
                color: "#fff",
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
              },
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              minWidth: 280,
              cursor: "default",
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              {/* Label row with inline info icon + refresh */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[300],
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  M-PESA Account Balance
                </Typography>
                <InfoOutlinedIcon
                  sx={{ fontSize: 13, color: colors.grey[300] }}
                />
                <IconButton
                  size="small"
                  onClick={() => triggerLiveBalanceRefresh(companyId)}
                  disabled={liveBalances.some((e) => e.refreshing)}
                  sx={{ ml: "auto", p: 0.4 }}
                  aria-label="Refresh account balance"
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 16,
                      color: colors.grey[300],
                    }}
                  />
                </IconButton>
              </Box>

              {/* One row per distinct paybill */}
              {liveBalances.length === 0 && !balanceLoadError ? (
                <CircularProgress size={20} sx={{ mt: 0.5 }} />
              ) : liveBalances.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.4, color: colors.redAccent[400] }}
                >
                  {balanceLoadError}
                </Typography>
              ) : (
                liveBalances.map((entry) => {
                  const shown = entry.branchNames.slice(0, 5);
                  const remaining = entry.branchNames.length - shown.length;
                  const branchLabel = shown.length
                    ? shown.join(", ") + (remaining > 0 ? ` +${remaining} more` : "")
                    : "Default (all other branches)";
                  const fullList = entry.branchNames.join(", ") || branchLabel;

                  return (
                    <Box key={entry.branchId ?? "default"} sx={{ mt: 0.6 }}>
                      <Tooltip title={fullList} placement="right" arrow>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.grey[300], display: "block" }}
                        >
                          {branchLabel}
                        </Typography>
                      </Tooltip>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ color: colors.greenAccent[400] }}
                        >
                          KES {formattedBalance(entry.utility)}
                        </Typography>
                        {entry.refreshing && <CircularProgress size={12} />}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: entry.error ? colors.redAccent[400] : colors.grey[400],
                          fontStyle: "italic",
                          lineHeight: 1.3,
                        }}
                      >
                        {entry.error
                          ? entry.error
                          : entry.receivedAt
                            ? `Live as of ${new Date(entry.receivedAt).toLocaleString()}`
                            : entry.refreshing
                              ? "Querying Safaricom…"
                              : "Ensure balance is adequate before disbursing loans."}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Tooltip>

        {/* Total Amount to Disburse Card */}
        <Tooltip
          title="Total amount to disburse across all loans currently awaiting company approval in this queue. Updates automatically as loans are approved or rejected."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
                color: "#fff",
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
              },
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              minWidth: 280,
              cursor: "default",
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PaymentsIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[300],
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Total to Disburse
                </Typography>
                <InfoOutlinedIcon
                  sx={{ fontSize: 13, color: colors.grey[300] }}
                />
              </Box>

              {loadingTotalToDisburse ? (
                <CircularProgress size={20} sx={{ mt: 0.5 }} />
              ) : (
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: colors.greenAccent[400] }}
                >
                  KES {formattedBalance(totalToDisburse)}
                </Typography>
              )}

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.4,
                  color: colors.grey[400],
                  fontStyle: "italic",
                  lineHeight: 1.3,
                }}
              >
                Across all loans pending company approval.
              </Typography>
            </Box>
          </Paper>
        </Tooltip>

        {/* Sababisha Company Wallet Card */}
        {isSababishaPaybillEnabled && (
        <Tooltip
          title="Sababisha Global Paybill central company wallet. For opted-in companies, loan disbursements are escrowed and disbursed through this shared balance with zero upfront Safaricom shortcode management."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
                color: "#fff",
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
              },
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              minWidth: 320,
              cursor: "default",
              border: walletInfo.isBelowThreshold ? "1px solid #d32f2f" : "none",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#1766A2",
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[300],
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Sababisha Wallet
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                  onClick={() => navigate("/wallet-management")}
                  sx={{
                    p: 0,
                    minWidth: "auto",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#1766A2",
                    textTransform: "none",
                  }}
                >
                  Manage
                </Button>
              </Box>

              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: walletInfo.isBelowThreshold ? colors.redAccent[400] : "#1766A2" }}
              >
                KES {formattedBalance(walletInfo.availableBalance)}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                <Typography
                  variant="caption"
                  sx={{ color: colors.grey[400], fontSize: "0.72rem" }}
                >
                  Escrowed: KES {formattedBalance(walletInfo.escrowedBalance)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Tooltip>
        )}
      </Box>

      <DynamicTableForJson
        title="Appraise Loans"
        subtitle="Loan Appraisal"
        base_url={base_url}
        columns={columns}
        actions={actions}
        onDataChanged={() => {
          triggerLiveBalanceRefresh(companyId);
          fetchTotalToDisburse();
        }}
      />
    </Box>
  );
};

export default CompanyLoanAppraisal;
